import { ErrorInfo } from "react";
import * as Sentry from "@sentry/react";

export interface ErrorLogEntry {
  error: Error;
  errorInfo?: ErrorInfo;
  timestamp: Date;
  url: string;
  userAgent: string;
  userId?: string;
  additionalContext?: Record<string, any>;
}

class ErrorLogger {
  private static instance: ErrorLogger;
  private logs: ErrorLogEntry[] = [];

  private constructor() {}

  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  logError(
    error: Error,
    errorInfo?: ErrorInfo,
    additionalContext?: Record<string, any>,
  ) {
    const logEntry: ErrorLogEntry = {
      error,
      errorInfo,
      timestamp: new Date(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      additionalContext,
    };

    // Store locally
    this.logs.push(logEntry);

    // Log to console in development
    if (import.meta.env.DEV) {
      console.group("🚨 Error Logged");
      console.error("Error:", error);
      console.error("Error Info:", errorInfo);
      console.error("Additional Context:", additionalContext);
      console.error("Full Log Entry:", logEntry);
      console.groupEnd();
    }

    // Send to Sentry
    Sentry.withScope((scope) => {
      if (additionalContext) {
        scope.setExtras(additionalContext);
      }
      if (errorInfo?.componentStack) {
        scope.setExtra("componentStack", errorInfo.componentStack);
      }
      Sentry.captureException(error);
    });
  }

  getLogs(): ErrorLogEntry[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
  }

  getLogsSummary(): { count: number; lastError?: Date } {
    return {
      count: this.logs.length,
      lastError:
        this.logs.length > 0
          ? this.logs[this.logs.length - 1].timestamp
          : undefined,
    };
  }
}

export const errorLogger = ErrorLogger.getInstance();

// Global error handler for unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  errorLogger.logError(
    new Error(`Unhandled Promise Rejection: ${event.reason}`),
    undefined,
    { type: "unhandledrejection", reason: event.reason },
  );
});

// Global error handler for uncaught errors
window.addEventListener("error", (event) => {
  errorLogger.logError(event.error || new Error(event.message), undefined, {
    type: "uncaughtError",
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

export default errorLogger;
